import { coerceBooleanProperty } from '@angular/cdk/coercion';
import {
  Component,
  computed,
  ElementRef,
  input,
  OnChanges,
  OnInit,
  SimpleChanges,
  ViewChild,
} from '@angular/core';
import { NotificationService } from '@app/core/services/notification.service';
import { loggerFor } from '@app/utils/logger';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { BehaviorSubject, Subject } from 'rxjs';
import { share } from 'rxjs/operators';

@UntilDestroy()
@Component({
  selector: 'portal-play-file',
  templateUrl: './play-file.component.html',
  styles: [],
  standalone: false,
})
export class PlayFileComponent implements OnInit, OnChanges {
  private static loading?: string;
  private static playing?: string;
  private static stopPlayingSubject: Subject<void> = new Subject();
  private static stopPlaying$ = PlayFileComponent.stopPlayingSubject.pipe(share());

  allowDownload = input(false, { transform: coerceBooleanProperty });
  file = input.required<string>();
  showBar = input(false, { transform: coerceBooleanProperty });

  progress$: Subject<number> = new BehaviorSubject(0);
  audioSource?: string = undefined;
  radius: number = 18;
  @ViewChild('audio', { static: false })
  private audioElement: ElementRef<HTMLAudioElement>;
  private logger = loggerFor('PlayFileComponent');

  // Firefox caches audio sources (open bug #1129121 - https://bugzilla.mozilla.org/show_bug.cgi?id=1129121).
  // Adding a query parameter to prevent it from doing that.
  private fileWithTimestamp = computed(() => {
    const filename = this.file();

    if (filename?.startsWith('blob:') === false) {
      return `${filename}?time=${new Date().getTime()}`;
    }

    return filename;
  });

  constructor(private notificationService: NotificationService) {
    // Listen to the stop playing subject until this component is destroyed
    PlayFileComponent.stopPlaying$.pipe(untilDestroyed(this)).subscribe(() => {
      if (this.audioElement !== undefined) {
        // Stop the playback
        this.nativeAudio.pause();
        this.nativeAudio.currentTime = 0;
      }
    });
  }

  get loading() {
    return this.fileWithTimestamp() !== undefined && PlayFileComponent.loading === this.fileWithTimestamp();
  }

  get playing() {
    return this.fileWithTimestamp() !== undefined && PlayFileComponent.playing === this.fileWithTimestamp();
  }

  get nativeAudio(): HTMLAudioElement {
    return this.audioElement?.nativeElement;
  }

  ngOnInit() {}

  ngOnChanges(changes: SimpleChanges): void {
    if ('file' in changes && changes.file.firstChange === false) {
      this.stopPlayback();
    }
  }

  /**
   * Play the given file.
   * If something is currently playing it will emit an event that stops the currently playing file.
   */
  togglePlayback() {
    const f = () => {
      this.audioSource = this.fileWithTimestamp();

      // Wait for angular to set the audio source and then toggle the playback
      setTimeout(() => {
        PlayFileComponent.loading = this.fileWithTimestamp();

        this.nativeAudio
          .play()
          .then(() => {
            PlayFileComponent.playing = this.fileWithTimestamp();
          })
          .catch((error) => {
            this.logger.error(error);
            this.notificationService.showDanger('common.playback.error');
          })
          .finally(() => {
            PlayFileComponent.loading = undefined;
          });
      }, 0);
    };

    this.stopPlayback();

    if (PlayFileComponent.playing !== this.fileWithTimestamp()) {
      setTimeout(() => {
        f();
      }, 0);
    }

    PlayFileComponent.playing = undefined;
  }

  /**
   * Playback ended
   */
  ended() {
    PlayFileComponent.playing = undefined;

    this.progress$.next(0);
  }

  /**
   * Progress of the native audio element changed. Update the progress accordingly
   */
  updateProgress() {
    const progress = (this.nativeAudio.currentTime / this.nativeAudio.duration) * 100 || 0;

    this.progress$.next(progress);
  }

  /**
   * Emit an event that stops all currently playing files
   */
  private stopPlayback() {
    PlayFileComponent.stopPlayingSubject.next();
  }
}
